import { useState } from 'react';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/nextjs';
// @mui
import {
  Paper,
  Button,
  Stack,
  Container,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Box,
  Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/Iconify';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// utils
import axios from '../../utils/axios';
import { fKrMdEKST } from '../../utils/formatTime';
// components
import MeetingScheduler from '../../components/meeting/MeetingScheduler';

export default function PreMeetingCalendarForm({ jobPost, candidate, reload }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();
  const [notAvailableSchedule, setNotAvailableSchedule] = useState(false);
  const [timeComment, setTimeComment] = useState('');
  const [timeCommentEmpty, setTimeCommentEmpty] = useState(false);
  const [timeCommentOverLength, setTimeCommentOverLength] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [scheduledMeeting, setScheduledMeeting] = useState(null);
  const [showMeetingInfo, setShowMeetingInfo] = useState(false);

  const getNextDays = (date = new Date()) => {
    const DAY_OF_FRIDAY = 5;
    const dateCopy = new Date(date.getTime());
    const addWeek = dateCopy.getDay() > DAY_OF_FRIDAY ? 0 : 7;
    const dayToNextDay = (7 - dateCopy.getDay() + DAY_OF_FRIDAY) % 7;
    const nextFriday = new Date(dateCopy.setDate(
      dateCopy.getDate() + addWeek + dayToNextDay
    ));

    const nextMonday = new Date(nextFriday);
    nextMonday.setDate(nextMonday.getDate() - 4);

    const nextWednesday = new Date(nextFriday);
    nextWednesday.setDate(nextWednesday.getDate() - 2);

    return {nextMonday, nextWednesday, nextFriday};
  }

  const { nextMonday, nextWednesday, nextFriday } = getNextDays();
  const nextSaturday = new Date(nextFriday);
  nextSaturday.setDate(nextSaturday.getDate() + 1);
  const reScheduleLimit = fKrMdEKST(nextSaturday);
  const toMonthStr = nextMonday.getMonth() !== nextWednesday.getMonth() ? `${nextWednesday.getMonth()+1}월 ` : ''
  const notAvailableSchedulePlaceholder = `예) ${nextMonday.getMonth()+1}월 ${nextMonday.getDate()}일 ~ ${toMonthStr}${nextWednesday.getDate()}일에 미팅 가능하신 다른 시간을 알려주세요!`;

  const handleTimeComment = (e) => {
    setTimeComment(e.target.value);
    setTimeCommentEmpty(!e.target.value);
    setTimeCommentOverLength(e.target.value.length > 100);
  };

  const openRequestChatDialog = () => {
    setNotAvailableSchedule(true);
  };

  const closeRequestChatDialog = () => {
    setNotAvailableSchedule(false);
    setTimeComment('');
    setTimeCommentEmpty(false);
    setTimeCommentOverLength(false);
  };

  const requestOtherTimes = async () => {
    if (!timeComment) {
      setTimeCommentEmpty(true);
      return ;
    }
    if (timeComment.length > 100) {
      setTimeCommentOverLength(true);
      return ;
    }
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const candidateId = candidate?.id;
      await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/request-other-times/`,
        {time_comment: timeComment}
      );
      enqueueSnackbar('미팅시간 변경 요청을 보냈습니다');
      closeRequestChatDialog();
      reload();
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      console.error(error);
      Sentry.captureException(error);
      enqueueSnackbar('미팅시간 변경 요청에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  const handleMeetingScheduled = (result) => {
    setScheduledMeeting(result);
    setShowMeetingInfo(true);
    // 더 긴 시간 후에 reload하여 정보를 확인할 수 있게 함
    setTimeout(() => reload(), 5000);
  };

  return (
    <>
      {candidate?.pre_meeting_calendar_code && (
      <>
        <Container
          id="meeting-scheduler-title"
          maxWidth="laptop"
          sx={{ p:1, mb: { xs: 0, sm: 1 } }}
        >
          <Grid
            maxWidth="laptop"
            container
            sx={{ p:1, direction: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}
          >
            <Stack>
              <Typography variant="h5" gutterBottom>
                크루와 사전 미팅을 진행할 날짜/시간을 선택해주세요.
              </Typography>
              <Typography variant="body1">
                아래 달력에는 크루의 미팅 가능한 시간이 표시되어 있습니다. (현지시간 기준)
              </Typography>
            </Stack>
            <div>
              {smUp && (
                <Button variant="outlined" onClick={openRequestChatDialog}>
                  다른 미팅 시간 요청하기
                </Button>
              )}
            </div>
          </Grid>
        </Container>
        {!smUp && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" variant="outlined" onClick={openRequestChatDialog}>
            다른 미팅 시간 요청하기
          </Button>
        </Box>
        )}
        <Paper
          sx={{ p: 2, minWidth: '320px', minHeight: smUp ? '600px' : '800px' }}
        >
          <MeetingScheduler
            calendarCode={candidate?.pre_meeting_calendar_code}
            user={user}
            onComplete={handleMeetingScheduled}
            title=""
            description={""}
            additionalQuestions={`${jobPost?.id},${candidate?.id},${user?.employee.company[0].name}`}
            duration={40}
            showTimezone={true}
          />
        </Paper>
      </>
      )}

      <Dialog open={notAvailableSchedule} fullWidth maxWidth="xs" onClose={closeRequestChatDialog}>
        <DialogTitle sx={{ px: 4, pt: 4, pb: 1 }}>달력에 미팅 가능한 시간이 없나요?</DialogTitle>
        <DialogContent sx={{ px: 4, py: 0 }}>
          <Stack sx={{ mt: 1, mb: 2 }}>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'error.main' }} />
              <Typography variant="subtitle1" sx={{ color: 'error.main', wordBreak: "keep-all" }}>
                {reScheduleLimit} 이전, 19시 전으로 요청해주세요.
              </Typography>
            </Stack>
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={timeComment}
            onChange={handleTimeComment}
            placeholder={notAvailableSchedulePlaceholder}
            error={timeCommentEmpty || timeCommentOverLength}
            helperText={timeCommentEmpty ? '내용을 입력해주세요' : timeCommentOverLength ? '100자 이내로 입력해주세요' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ m: 1 }}>
          <LoadingButton
            size="large"
            sx={{ px: 5 }}
            onClick={closeRequestChatDialog}
            loading={isSubmit}
          >
            취소
          </LoadingButton>
          <LoadingButton
            variant="contained"
            size="large"
            sx={{ px: 5 }}
            onClick={requestOtherTimes}
            loading={isSubmit}
          >
            요청하기
          </LoadingButton>
        </DialogActions>
      </Dialog>
      
      {/* 미팅 예약 완료 정보 다이얼로그 */}
      <Dialog open={showMeetingInfo} fullWidth maxWidth="sm">
        <DialogTitle sx={{ px: 4, pt: 4, pb: 1 }}>사전 미팅 예약 완료</DialogTitle>
        <DialogContent sx={{ px: 4, py: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            미팅이 성공적으로 예약되었습니다!
          </Alert>
          
          {scheduledMeeting && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                미팅 정보
              </Typography>
              
              {scheduledMeeting.scheduled_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    미팅 시간
                  </Typography>
                  <Typography variant="body1">
                    {new Date(scheduledMeeting.scheduled_at).toLocaleString('ko-KR')}
                  </Typography>
                </Box>
              )}
              
              {scheduledMeeting.zoom_meeting_url && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Zoom 링크
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    <a href={scheduledMeeting.zoom_meeting_url} target="_blank" rel="noopener noreferrer">
                      {scheduledMeeting.zoom_meeting_url}
                    </a>
                  </Typography>
                  {scheduledMeeting.zoom_meeting_password && (
                    <Typography variant="caption" color="text.secondary">
                      비밀번호: {scheduledMeeting.zoom_meeting_password}
                    </Typography>
                  )}
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary">
                예약 확인 메일이 발송되었습니다.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ m: 1 }}>
          <Button
            variant="contained"
            size="large"
            sx={{ px: 5 }}
            onClick={() => {
              setShowMeetingInfo(false);
              reload();
            }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
